/**
 * NOLAS 2026 (Mikaël FOURNY).
 * src/i18n/applyIdentityTokens.ts
 *
 * Rôle :
 *   Appliquer les jetons d'IDENTITÉ de l'événement ({eventName}, {eventTypeAt},
 *   {eventPlace}, {organizer}...) au catalogue i18n, UNE fois, au-dessus de
 *   react-intl. Exactement ce que `applyLexiconToMessages` fait déjà pour les
 *   jetons de vocabulaire {lex*}.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * POURQUOI CE FICHIER EXISTE : la moitié du dispositif manquait
 * ─────────────────────────────────────────────────────────────────────────
 *   Le projet a DEUX familles de jetons dans son catalogue, et les traitait de
 *   deux façons opposées :
 *
 *     • les jetons de vocabulaire {lex*} sont substitués STRUCTURELLEMENT,
 *       au-dessus de react-intl (LexiconIntlProvider). Un écran ne peut pas se
 *       tromper : il n'a rien à faire.
 *
 *     • les jetons d'identité {eventName}, {eventTypeAt}... reposaient sur la
 *       DISCIPLINE : chaque site d'appel devait écrire `values={identity}`.
 *       `values` est facultatif par signature, TypeScript ne dit rien, et
 *       personne ne voit l'oubli avant qu'un visiteur ne le lise à l'écran.
 *
 *   L'oubli a eu lieu. Sur la page publique de choix de profil, une carte
 *   affichait littéralement « Vous animez un atelier {eventTypeAt}
 *   {eventName}. » : deux jetons techniques bruts, sur le premier écran du
 *   parcours d'inscription. Le rendu des cartes appelle `FormattedMessage`
 *   sans `values`, pour toutes les cartes à la fois.
 *
 *   Corriger ce seul site d'appel aurait réparé le symptôme et laissé la
 *   cause : le même oubli reste possible partout ailleurs, et à chaque
 *   nouvel écran écrit par quelqu'un qui ignore la règle. On aligne donc
 *   l'identité sur le vocabulaire : substitution unique, au-dessus de
 *   react-intl, plus rien à ne pas oublier.
 *
 * Zéro régression :
 *   Les sites d'appel qui passent déjà `values={identity}` continuent de
 *   fonctionner : react-intl ignore simplement des valeurs qui ne
 *   correspondent plus à aucun jeton du message. Les jetons non liés à
 *   l'identité ({count}, {minutes}, {br}, {role}...) ne sont pas touchés,
 *   puisque seuls les noms présents dans la table d'identité sont substitués.
 *
 * Réutilisable : 100 %, aucune dépendance à un événement précis.
 *
 * Mikael - chef de projet - 2026-09-09
 */

/** Repère un jeton simple `{unNom}` dans une chaîne du catalogue. */
const JETON_SIMPLE = /\{([A-Za-z][A-Za-z0-9_]*)\}/g;

/**
 * Applique la table d'identité à UN message.
 *
 * Un nom absent de la table est laissé TEL QUEL, jamais remplacé par du vide :
 * react-intl continuera de le traiter comme un vrai placeholder, alimenté par
 * le `values` du site d'appel. C'est ce qui permet à `{count}`, `{minutes}` ou
 * `{br}` de cohabiter sans dommage avec `{eventName}`.
 *
 * Mikael - chef de projet - 2026-09-09
 */
export function applyIdentityToMessage(
  message: string,
  jetons: Readonly<Record<string, string>>,
): string {
  if (!message.includes('{')) return message;
  return message.replace(JETON_SIMPLE, (entier, nom: string) => {
    const valeur = jetons[nom];
    return typeof valeur === 'string' ? valeur : entier;
  });
}

/**
 * Applique la table d'identité à TOUT le catalogue.
 *
 * Les messages sans accolade sont renvoyés PAR RÉFÉRENCE : sur un catalogue de
 * plusieurs milliers d'entrées, seules celles qui portent un jeton coûtent
 * quelque chose.
 *
 * @param messages Catalogue source, jamais muté.
 * @param jetons   Table plate `{ eventName: 'X', eventTypeAt: 'au ...' }`.
 *
 * Mikael - chef de projet - 2026-09-09
 */
export function applyIdentityToMessages(
  messages: Readonly<Record<string, string>>,
  jetons: Readonly<Record<string, string>>,
): Record<string, string> {
  const sortie: Record<string, string> = {};
  for (const cle of Object.keys(messages)) {
    const valeur = messages[cle] as string;
    sortie[cle] = applyIdentityToMessage(valeur, jetons);
  }
  return sortie;
}
